// The report's draft (spec 054 T036, T064, T067; research R7).
//
// Rows added before any location is chosen (owner decision, 2026-09-15) live in
// the browser, because a draft is a location's. They are the report's, not a
// location's: the first location chosen takes them, joining its open draft if
// it has one and creating it otherwise. They are never dropped (art. III).
//
// Visiting "Raport nou" creates nothing (FR-023). Once a location is bound, the
// page loads that location's open draft if it has one; otherwise the report
// lives in the browser until its first row, and that row creates the draft in
// one call, `POST /stock/drafts {siteId, rows: [row]}`. A draft opened from
// "Raporturi nefinalizate" is loaded by its id (`/:role/reporting/drafts/:id`).
//
// Every later change is ONE row operation, add, replace or remove keyed by
// clientRowId, saved through the debounced PATCH. When another tab or device
// saved the draft first (VERSION_CONFLICT), the operations not yet saved are
// re-applied to the newer draft and saved again; the rows this tab held are
// never resent as a whole, which would put back what the other tab removed and
// drop what it added. An operation that no longer applies, an edit of a row the
// other tab removed, is handed back as a conflict for the page to say and for
// the dialog to offer again: a change is never lost in silence (research R7;
// constitution art. III, nothing a user entered is dropped, and art. IV, no
// failure is silent).
import { useEffect, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { isNormalizedApiError, onSessionRedirect } from '../../app/api-client';
import {
  confirmDraft,
  createDraftWithRows,
  deleteDraft,
  findOpenDraft,
  getDraft,
  upsertDraft,
  type DraftDefaultReasons,
  type DraftRow,
  type DraftState,
  type SubmissionResponse,
} from './api';
import { handOffUnsaved, takeHandOffs, type DraftHandOff } from './draft-handoff';

const DEBOUNCE_MS = 500;

/** The root of every draft key; what invalidating "the report" means. */
export const DRAFT_QUERY_ROOT = 'stock-draft';

/**
 * The cache key of a location's report (T036 retires the dead
 * `['stock-draft', 'current']`). Per location, because a draft IS per location:
 * (operator, registrar, site) is unique while open, so picking another
 * location opens that location's own report. `unbound` is the operator's draft
 * that names no location, which only drafts from before spec 054 produce.
 */
export function draftQueryKey(siteId: string | null): readonly [string, string] {
  return [DRAFT_QUERY_ROOT, siteId ?? 'unbound'];
}

/** The cache key of a draft opened by its id, from the drafts list (T064). */
export function draftByIdKey(draftId: string): readonly [string, string] {
  return [DRAFT_QUERY_ROOT, `id:${draftId}`];
}

export type SyncState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; savedAt: Date }
  | { kind: 'error'; message: string };

/** One dialog action on the report, keyed by the row's clientRowId (R7). */
export type RowOperation =
  | { kind: 'add'; row: DraftRow }
  | { kind: 'replace'; row: DraftRow }
  | { kind: 'remove'; clientRowId: string };

/** An edit that could not be re-applied: its row was removed elsewhere. */
export interface RowConflict {
  /** The row as this tab last had it, for the dialog to offer again. */
  row: DraftRow;
}

/** What binding a location answered: the draft now on screen, and whose it is. */
export interface BindSiteResult {
  draft: DraftState;
  /**
   * True when the location already had a draft (409 SITE_HAS_DRAFT) and that
   * draft was opened instead: rows never move between locations (spec 028 Q1).
   */
  openedExisting: boolean;
}

interface UpsertVariables {
  key: readonly [string, string];
  id: string;
  rows: DraftRow[];
  expectedVersion: number;
  defaultReasons: DraftDefaultReasons | null;
}

export interface UseDraftReturn {
  /** The open draft; null while the report exists only in the browser; undefined while loading. */
  draft: DraftState | null | undefined;
  /** What the report holds: the draft's rows, or the rows the browser holds before it exists. */
  rows: DraftRow[];
  /**
   * The rows as the server last confirmed them (spec 054 T097): what it answered
   * the last load or save with, before any change still on its way. After a save
   * that failed, the rows on screen keep the operator's change while these keep
   * what the server holds, its row errors included (constitution art. IV).
   */
  serverRows: DraftRow[];
  /** A change on screen that no save has confirmed yet: waiting, in flight, or refused. */
  unsaved: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  syncState: SyncState;
  addRow: (row: DraftRow) => void;
  replaceRow: (row: DraftRow) => void;
  removeRow: (clientRowId: string) => void;
  /** The edit a concurrent save left with no row to apply to; null when there is none. */
  conflict: RowConflict | null;
  dismissConflict: () => void;
  /** Sends again what a failed save left pending (a network failure, not a conflict). */
  retrySave: () => void;
  /**
   * "Golește raportul", once confirmed (FR-021, research R7): deletes the draft,
   * so a draft never exists with zero rows, and leaves the report "Nou". A
   * report that has no draft yet only lets go of the rows the browser holds.
   */
  discardMutation: UseMutationResult<void, unknown, void>;
  /** The last reason used in each direction, as the draft stores it (R4). */
  defaultReasons: DraftDefaultReasons | null;
  /** Remembers the defaults; they travel with the draft's next save. */
  rememberDefaultReasons: (next: DraftDefaultReasons) => void;
  confirmMutation: UseMutationResult<SubmissionResponse, unknown, void>;
  /**
   * Bind the operator's unbound draft to a storage location, carrying its rows.
   *
   * The ONLY caller of `upsertDraft`'s `siteId`, and deliberately so: rows never
   * move from one location to another (specs/028-raport-nou-layout Q1), so a
   * change of location is a change of draft, never a change of this field. A
   * location that already has a draft answers 409 SITE_HAS_DRAFT, and that
   * draft is opened instead (T063).
   */
  bindSiteMutation: UseMutationResult<BindSiteResult, unknown, string>;
}

function messageOf(err: unknown): string {
  return (err as { message?: string })?.message ?? 'Eroare necunoscută';
}

function hasRow(rows: readonly DraftRow[], clientRowId: string): boolean {
  return rows.some((row) => row.clientRowId === clientRowId);
}

/** One operation on the rows as they are; `applied` is false when it no longer fits them. */
function applyOperation(
  rows: DraftRow[],
  operation: RowOperation,
): { rows: DraftRow[]; applied: boolean } {
  switch (operation.kind) {
    case 'add':
      // The same row twice is the same row: a retry must not duplicate it.
      if (hasRow(rows, operation.row.clientRowId))
        return {
          rows: rows.map((row) =>
            row.clientRowId === operation.row.clientRowId ? operation.row : row,
          ),
          applied: true,
        };
      return { rows: [...rows, operation.row], applied: true };
    case 'replace':
      if (!hasRow(rows, operation.row.clientRowId)) return { rows, applied: false };
      return {
        rows: rows.map((row) =>
          row.clientRowId === operation.row.clientRowId ? operation.row : row,
        ),
        applied: true,
      };
    case 'remove':
      // Already gone is what was asked: nothing to apply, nothing lost.
      if (!hasRow(rows, operation.clientRowId)) return { rows, applied: false };
      return {
        rows: rows.filter((row) => row.clientRowId !== operation.clientRowId),
        applied: true,
      };
  }
}

/**
 * The rebase of R7: the operations this tab had not saved yet, applied to the
 * rows another save left. `applied` is what still has to be saved; `lost` is
 * every edit whose row is gone, which the caller must say and offer back.
 */
export function rebaseOperations(
  rows: readonly DraftRow[],
  operations: readonly RowOperation[],
): { rows: DraftRow[]; applied: RowOperation[]; lost: DraftRow[] } {
  let current = [...rows];
  const applied: RowOperation[] = [];
  const lost: DraftRow[] = [];
  for (const operation of operations) {
    const out = applyOperation(current, operation);
    current = out.rows;
    if (out.applied) applied.push(operation);
    else if (operation.kind === 'replace') lost.push(operation.row);
  }
  return { rows: current, applied, lost };
}

/** The cache every copy of one draft lives under: its location's report and its own id. */
function storeDraft(qc: QueryClient, draft: DraftState): void {
  qc.setQueryData(draftQueryKey(draft.siteId || null), draft);
  qc.setQueryData(draftByIdKey(draft.id), draft);
}

/** A draft that is gone, or not the caller's (the same 404 for both), is no draft. */
async function draftOrNull(id: string): Promise<DraftState | null> {
  try {
    return await getDraft(id);
  } catch (err) {
    if ((err as { status?: number })?.status === 404) return null;
    throw err;
  }
}

export function useDraft(siteId: string | null, draftId: string | null = null): UseDraftReturn {
  const qc = useQueryClient();
  const [syncState, setSyncState] = useState<SyncState>({ kind: 'idle' });
  const [conflict, setConflict] = useState<RowConflict | null>(null);
  const key = draftId ? draftByIdKey(draftId) : draftQueryKey(siteId);
  // The draft as the server last answered it (T097). The cache holds the rows
  // on screen, changes not yet saved included, so the two are kept apart.
  const [confirmed, setConfirmed] = useState<DraftState | null>(null);
  const loadDraft = async (): Promise<DraftState | null> => {
    const loaded = await (draftId ? draftOrNull(draftId) : findOpenDraft(siteId));
    setConfirmed(loaded);
    return loaded;
  };

  const draftQuery = useQuery({
    queryKey: key,
    queryFn: loadDraft,
    staleTime: Infinity,
    // Opening the report reads the draft as it is now, not as this tab last
    // cached it: another tab may have saved it meanwhile.
    refetchOnMount: 'always',
  });

  // Rows the browser holds before the draft exists: while the create is in
  // flight, and after a create that failed. Kept, never dropped (constitution
  // art. III); the next operation tries the create again.
  const [localRows, setLocalRowsState] = useState<DraftRow[] | null>(null);
  const localRowsRef = useRef<DraftRow[] | null>(null);
  const creatingRef = useRef(false);
  // Operations that arrived while the create was in flight, replayed on the
  // draft it returns.
  const queuedRef = useRef<RowOperation[]>([]);
  // Bumped on every change of location, so a create that answers after the
  // operator moved on files its draft without touching the new report.
  const generationRef = useRef(0);

  // Operations on screen and not yet sent, and those the save in flight carries.
  const pendingOpsRef = useRef<RowOperation[]>([]);
  // How many are waiting, for the screen: a ref alone would not re-render it.
  const [pendingCount, setPendingCount] = useState(0);
  const sentOpsRef = useRef<RowOperation[]>([]);
  const pendingDefaultsRef = useRef<DraftDefaultReasons | null>(null);
  const sentDefaultsRef = useRef<DraftDefaultReasons | null>(null);
  const [localDefaults, setLocalDefaults] = useState<DraftDefaultReasons | null>(null);
  const savingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The operations a rebase took off the pending set and has not re-applied yet:
  // still unsaved, so still handed off if the session expires meanwhile.
  const rebasingOpsRef = useRef<RowOperation[]>([]);

  function setLocalRows(rows: DraftRow[] | null): void {
    localRowsRef.current = rows;
    setLocalRowsState(rows);
  }

  function setPending(operations: RowOperation[]): void {
    pendingOpsRef.current = operations;
    setPendingCount(operations.length);
  }

  // Rows entered with no location, on their way to the first one chosen: held
  // until that location's draft lookup has answered, then added to its draft or
  // made its first rows.
  const carriedRef = useRef<DraftRow[] | null>(null);
  // Defaults set with no location, on their way to the first one chosen.
  const carriedDefaultsRef = useRef(false);
  const lastSiteRef = useRef(siteId);

  // Another location is another report: what the browser held for the last one
  // stays with it, and so do the defaults its draft remembers. Except rows
  // entered before any location was chosen, which were entered for none: they
  // go with the report to its first location, with the defaults they taught.
  // So do the defaults set in "Motive implicite" before any location was chosen,
  // rows or no rows: caiet UC50 step 2 comes before step 3
  // (docs/shape-ops-default-reasons.md §Decision 4).
  useEffect(() => {
    const previous = lastSiteRef.current;
    lastSiteRef.current = siteId;
    generationRef.current += 1;
    queuedRef.current = [];
    const held = localRowsRef.current;
    const firstLocation = previous === null && siteId !== null && !draftId;
    const carried = firstLocation && held && held.length > 0 ? held : null;
    carriedRef.current = carried;
    if (carried) {
      setSyncState({ kind: 'saving' });
      return;
    }
    setLocalRows(null);
    if (firstLocation && pendingDefaultsRef.current !== null) {
      carriedDefaultsRef.current = true;
      return;
    }
    setLocalDefaults(null);
    pendingDefaultsRef.current = null;
  }, [siteId]);

  // Drafts "Golește raportul" deleted: a save that answers afterwards must not
  // put the deleted draft back on screen.
  const discardedRef = useRef<Set<string>>(new Set());

  function writeCache(target: readonly [string, string], draft: DraftState | null): void {
    qc.setQueryData(target, draft);
    if (draft) storeDraft(qc, draft);
  }

  // R7: the newer draft, what this tab had not saved re-applied to it, and
  // anything that no longer applies handed back rather than dropped.
  async function rebase(target: readonly [string, string]): Promise<void> {
    const operations = [...sentOpsRef.current, ...pendingOpsRef.current];
    sentOpsRef.current = [];
    setPending([]);
    rebasingOpsRef.current = operations;
    setSyncState({ kind: 'saving' });
    let fresh: DraftState | null;
    try {
      fresh = await qc.fetchQuery({ queryKey: target, queryFn: loadDraft, staleTime: 0 });
    } catch (err) {
      rebasingOpsRef.current = [];
      setPending(operations);
      setSyncState({ kind: 'error', message: messageOf(err) });
      return;
    }
    rebasingOpsRef.current = [];
    const base = fresh?.rows ?? [];
    const { rows, applied, lost } = rebaseOperations(base, operations);
    const last = lost.at(-1);
    if (last) setConflict({ row: last });
    if (!fresh) {
      // The draft is gone (saved or emptied elsewhere): what this tab was adding
      // starts a new one rather than vanishing with it.
      qc.setQueryData(target, null);
      if (rows.length > 0) {
        setLocalRows(rows);
        startCreate(rows);
      } else {
        setSyncState({ kind: 'idle' });
      }
      return;
    }
    writeCache(target, { ...fresh, rows });
    setPending(applied);
    if (applied.length > 0 || pendingDefaultsRef.current !== null) flush();
    else setSyncState({ kind: 'saved', savedAt: new Date() });
  }

  // Set when the page unmounted with a save on its way: that save may still meet
  // an expired session, so the hand-off stays registered until it settles.
  const releaseOnSettleRef = useRef<(() => void) | null>(null);
  function releaseAfterSave(): void {
    releaseOnSettleRef.current?.();
    releaseOnSettleRef.current = null;
  }

  const upsertMutation = useMutation({
    mutationFn: ({ id, rows, expectedVersion, defaultReasons }: UpsertVariables) =>
      upsertDraft(id, expectedVersion, rows, undefined, defaultReasons ?? undefined),
    // Offline, the save fails at once and says so, rather than waiting paused as
    // "saving" with nothing on screen; the `online` listener sends it again
    // (docs/shape-ops-draft-session-handoff.md §D3).
    networkMode: 'always',
    onMutate: ({ defaultReasons }) => {
      savingRef.current = true;
      sentDefaultsRef.current = defaultReasons;
      setSyncState({ kind: 'saving' });
    },
    onSuccess: (data, variables) => {
      savingRef.current = false;
      releaseAfterSave();
      sentOpsRef.current = [];
      if (discardedRef.current.has(variables.id)) return;
      // Operations that arrived mid-save are still on screen: keep them there
      // on top of what the server answered, and save them against its version.
      const { rows } = rebaseOperations(data.rows, pendingOpsRef.current);
      writeCache(variables.key, { ...data, rows });
      setConfirmed(data);
      setSyncState({ kind: 'saved', savedAt: new Date() });
      if (pendingDefaultsRef.current === sentDefaultsRef.current) pendingDefaultsRef.current = null;
      if (pendingOpsRef.current.length > 0 || pendingDefaultsRef.current !== null) flush();
    },
    onError: (err: unknown, variables) => {
      savingRef.current = false;
      releaseAfterSave();
      if (discardedRef.current.has(variables.id)) return;
      const status = (err as { status?: number })?.status;
      const code = (err as { code?: string })?.code;
      // 404: the draft was deleted elsewhere. VERSION_CONFLICT: a concurrent save
      // won the version race. Both rebase this tab's operations (R7).
      if (
        status === 404 ||
        code === 'VERSION_CONFLICT' ||
        (status === 409 && code !== 'SITE_HAS_DRAFT')
      ) {
        void rebase(variables.key);
        return;
      }
      // Anything else leaves the operations pending, on screen, for a retry;
      // what the server holds stays `confirmed`, errors and all (T097).
      setPending([...sentOpsRef.current, ...pendingOpsRef.current]);
      sentOpsRef.current = [];
      setSyncState({ kind: 'error', message: messageOf(err) });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const current = qc.getQueryData<DraftState | null>(key);
      if (!current) throw new Error('no draft');
      return confirmDraft(current.id, current.version);
    },
    // The draft is gone once saved (FR-029), and the report it became is now
    // both today's report and the newest of the last five Acasă lists.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [DRAFT_QUERY_ROOT] });
      void qc.invalidateQueries({ queryKey: ['drafts-count'] });
      void qc.invalidateQueries({ queryKey: ['drafts-register'] });
      void qc.invalidateQueries({ queryKey: ['home-today-submissions'] });
      void qc.invalidateQueries({ queryKey: ['home-recent-submissions'] });
    },
  });

  const bindSiteMutation = useMutation({
    mutationFn: async (nextSiteId: string): Promise<BindSiteResult> => {
      const current = qc.getQueryData<DraftState | null>(key);
      if (!current) throw new Error('no draft');
      try {
        const bound = await upsertDraft(current.id, current.version, current.rows, nextSiteId);
        return { draft: bound, openedExisting: false };
      } catch (err) {
        // The location already has a report: open that one rather than moving
        // these rows onto it (contracts/bff-stock-reporting.md, PATCH; T063).
        if (!isNormalizedApiError(err) || err.code !== 'SITE_HAS_DRAFT') throw err;
        const held = err.draftId ? await draftOrNull(err.draftId) : await findOpenDraft(nextSiteId);
        if (!held) throw err;
        return { draft: held, openedExisting: true };
      }
    },
    onSuccess: ({ draft, openedExisting }) => {
      storeDraft(qc, draft);
      setConfirmed(draft);
      if (!openedExisting) {
        // The bind carried every row on screen; nothing is pending any more, and
        // the operator has no unbound draft.
        setPending([]);
        if (timerRef.current) clearTimeout(timerRef.current);
        qc.setQueryData(draftQueryKey(null), null);
        if (draftId) qc.setQueryData(key, draft);
      }
      void qc.invalidateQueries({ queryKey: ['drafts-count'] });
    },
  });

  // Send what is pending iff nothing is in flight. While a save runs, its
  // onSuccess/onError flushes again, so saves never overlap and never reuse a
  // stale expectedVersion.
  function flush(): void {
    if (savingRef.current) return;
    const defaults = pendingDefaultsRef.current;
    if (pendingOpsRef.current.length === 0 && defaults === null) return;
    const current = qc.getQueryData<DraftState | null>(key);
    if (!current) return;
    sentOpsRef.current = pendingOpsRef.current;
    setPending([]);
    upsertMutation.mutate({
      key,
      id: current.id,
      rows: current.rows,
      expectedVersion: current.version,
      defaultReasons: defaults,
    });
  }

  function schedule(): void {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flush();
    }, DEBOUNCE_MS);
  }

  // Optimistic: the operation lands in the cache at once, and the rows are
  // always read from the cache rather than a closure, so rapid operations compose.
  function applyToDraft(operation: RowOperation): void {
    qc.setQueryData<DraftState | null>(key, (prev) => {
      if (!prev) return prev;
      return { ...prev, rows: applyOperation(prev.rows, operation).rows };
    });
    setPending([...pendingOpsRef.current, operation]);
    schedule();
  }

  function startCreate(rows: DraftRow[]): void {
    const target = siteId;
    if (!target) return;
    const generation = generationRef.current;
    const createKey = key;
    creatingRef.current = true;
    setSyncState({ kind: 'saving' });
    createDraftWithRows(target, rows).then(
      (created) => {
        creatingRef.current = false;
        writeCache(createKey, created);
        void qc.invalidateQueries({ queryKey: ['drafts-count'] });
        if (generation !== generationRef.current) return;
        setConfirmed(created);
        const queued = queuedRef.current;
        queuedRef.current = [];
        setLocalRows(null);
        setSyncState({ kind: 'saved', savedAt: new Date() });
        if (queued.length > 0) queued.forEach(applyToDraft);
        else if (pendingDefaultsRef.current !== null) schedule();
      },
      (err: unknown) => {
        creatingRef.current = false;
        if (generation !== generationRef.current) return;
        // The queued operations are already in localRows; the next one retries.
        queuedRef.current = [];
        setSyncState({ kind: 'error', message: messageOf(err) });
      },
    );
  }

  function operate(operation: RowOperation): void {
    const current = qc.getQueryData<DraftState | null>(key);
    // An edit of a row removed meanwhile adds it back rather than losing it
    // (constitution art. III).
    const effective: RowOperation =
      operation.kind === 'replace' &&
      !hasRow(current?.rows ?? localRowsRef.current ?? [], operation.row.clientRowId)
        ? { kind: 'add', row: operation.row }
        : operation;
    if (current) {
      applyToDraft(effective);
      return;
    }
    const rows = applyOperation(localRowsRef.current ?? [], effective).rows;
    if (creatingRef.current) {
      queuedRef.current.push(effective);
      setLocalRows(rows);
      return;
    }
    setLocalRows(rows.length > 0 ? rows : null);
    // A report with no rows is no draft (FR-023); nor is one with no location.
    if (rows.length > 0) startCreate(rows);
  }

  // The carried rows land once the location's draft is known: added to it
  // through the PATCH, or creating it with them (FR-023).
  useEffect(() => {
    const carried = carriedRef.current;
    if (!carried || draftQuery.isFetching) return;
    if (draftQuery.isError) {
      carriedRef.current = null;
      setSyncState({ kind: 'error', message: messageOf(draftQuery.error) });
      return;
    }
    carriedRef.current = null;
    if (qc.getQueryData<DraftState | null>(key)) {
      setLocalRows(null);
      carried.forEach((row) => applyToDraft({ kind: 'add', row }));
      return;
    }
    startCreate(carried);
  }, [draftQuery.isFetching, draftQuery.isError, siteId]);

  // An expired session's hand-off lands once the draft or location it names has
  // answered its first load, and is rebased onto it as R7 rebases a concurrent
  // save: what still applies is saved, an edit whose row is gone comes back as
  // the conflict (docs/shape-ops-draft-session-handoff.md §D2). Once per report,
  // so the page an expiry is tearing down never takes back what it just handed off.
  const recoveredForRef = useRef<string | null>(null);
  function recover(entry: DraftHandOff): void {
    if (entry.defaultReasons) {
      pendingDefaultsRef.current = entry.defaultReasons;
      setLocalDefaults(entry.defaultReasons);
    }
    const current = qc.getQueryData<DraftState | null>(key);
    const { rows, applied, lost } = rebaseOperations(
      current?.rows ?? localRowsRef.current ?? [],
      entry.operations,
    );
    const last = lost.at(-1);
    if (last) setConflict({ row: last });
    if (current) {
      writeCache(key, { ...current, rows });
      setPending([...pendingOpsRef.current, ...applied]);
      schedule();
      return;
    }
    if (rows.length === 0) return;
    setLocalRows(rows);
    if (creatingRef.current) queuedRef.current.push(...applied);
    else startCreate(rows);
  }
  useEffect(() => {
    if (draftQuery.isFetching || draftQuery.data === undefined) return;
    const loaded = draftQuery.data;
    // A draft opened by id gets its location from the page after it loads, and
    // that change of location resets the remembered reasons: land after it.
    if (draftId && loaded && (loaded.siteId || null) !== siteId) return;
    const report = key.join('|');
    if (recoveredForRef.current === report) return;
    recoveredForRef.current = report;
    takeHandOffs(
      (entry) =>
        (loaded !== null && entry.draftId === loaded.id) || (!draftId && entry.siteId === siteId),
    ).forEach(recover);
  }, [draftQuery.isFetching, draftQuery.data, siteId, draftId]);

  // The carried defaults are saved once the location's draft is known; with no
  // draft yet, they go with the save after its first row creates it (startCreate).
  useEffect(() => {
    if (!carriedDefaultsRef.current || draftQuery.isFetching) return;
    carriedDefaultsRef.current = false;
    if (!draftQuery.isError && qc.getQueryData<DraftState | null>(key)) schedule();
  }, [draftQuery.isFetching, draftQuery.isError, siteId]);

  const addRow = (row: DraftRow): void => operate({ kind: 'add', row });
  const replaceRow = (row: DraftRow): void => operate({ kind: 'replace', row });
  const removeRow = (clientRowId: string): void => operate({ kind: 'remove', clientRowId });

  const discardMutation = useMutation({
    mutationFn: async () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const current = qc.getQueryData<DraftState | null>(key);
      if (!current) return;
      discardedRef.current.add(current.id);
      try {
        await deleteDraft(current.id);
      } catch (err) {
        // Already gone is what was asked for.
        if ((err as { status?: number })?.status === 404) return;
        discardedRef.current.delete(current.id);
        throw err;
      }
    },
    onSuccess: () => {
      const current = qc.getQueryData<DraftState | null>(key);
      generationRef.current += 1;
      queuedRef.current = [];
      setPending([]);
      setConfirmed(null);
      setLocalRows(null);
      qc.setQueryData(key, null);
      if (current) {
        qc.setQueryData(draftQueryKey(current.siteId || null), null);
        qc.setQueryData(draftByIdKey(current.id), null);
      }
      setSyncState({ kind: 'idle' });
      void qc.invalidateQueries({ queryKey: ['drafts-count'] });
      void qc.invalidateQueries({ queryKey: ['drafts-register'] });
    },
    // The draft stays, and what was waiting to be saved is saved after all.
    onError: () => {
      if (pendingOpsRef.current.length > 0) schedule();
    },
  });

  const rememberDefaultReasons = (next: DraftDefaultReasons): void => {
    pendingDefaultsRef.current = next;
    setLocalDefaults(next);
    if (qc.getQueryData<DraftState | null>(key)) schedule();
  };

  // What the server has not confirmed, written to the tab before a 401 sends it
  // to sign in again (§D1). A discarded draft hands off nothing: its edits were
  // let go on purpose.
  function handOff(): void {
    const current = qc.getQueryData<DraftState | null>(key);
    if (current && discardedRef.current.has(current.id)) return;
    const operations: RowOperation[] = current
      ? [...rebasingOpsRef.current, ...sentOpsRef.current, ...pendingOpsRef.current]
      : (localRowsRef.current ?? []).map((row) => ({ kind: 'add', row }));
    const defaultReasons = pendingDefaultsRef.current;
    if (operations.length === 0 && defaultReasons === null) return;
    handOffUnsaved({
      draftId: current?.id ?? null,
      siteId: current ? current.siteId || null : siteId,
      operations,
      defaultReasons,
    });
  }

  // "Reîncearcă" and a returning connection: with a draft, send what is pending;
  // without one, retry the create the first rows are waiting for (§D3).
  function resend(): void {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (qc.getQueryData<DraftState | null>(key)) {
      flush();
      return;
    }
    const held = localRowsRef.current;
    if (held && held.length > 0 && !creatingRef.current && !carriedRef.current) startCreate(held);
  }

  // Leaving the page within the debounce still saves what was entered.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const handOffRef = useRef(handOff);
  handOffRef.current = handOff;
  const resendRef = useRef(resend);
  resendRef.current = resend;
  useEffect(() => {
    const release = onSessionRedirect(() => handOffRef.current());
    const onOnline = (): void => resendRef.current();
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        flushRef.current();
      }
      if (savingRef.current) releaseOnSettleRef.current = release;
      else release();
    };
  }, []);

  const draft = draftQuery.data;
  // The confirmed copy of THIS draft; another report's (a location changed, the
  // load still on its way) is none, and the cache speaks until the load answers.
  const serverRows = draft ? (confirmed?.id === draft.id ? confirmed.rows : draft.rows) : [];
  return {
    draft,
    rows: draft?.rows ?? localRows ?? [],
    serverRows,
    unsaved: pendingCount > 0 || syncState.kind === 'saving' || syncState.kind === 'error',
    isLoading: draftQuery.isLoading,
    isError: draftQuery.isError,
    error: draftQuery.error,
    syncState,
    addRow,
    replaceRow,
    removeRow,
    conflict,
    dismissConflict: () => setConflict(null),
    retrySave: resend,
    discardMutation,
    defaultReasons: localDefaults ?? draft?.defaultReasons ?? null,
    rememberDefaultReasons,
    confirmMutation,
    bindSiteMutation,
  };
}
